import {
  Bell,
  BellDot,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

type Notification = {
  id: string;
  title: string;
  description?: string;
  createdAt: string;
  read: boolean;
};

export function Notifications() {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'unread' | 'all'>('unread');
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: '1',
      title: 'Nova prestação enviada',
      description: 'Prestação 2025/01 foi enviada para análise.',
      createdAt: '2025-12-10T09:00:00Z',
      read: false,
    },
    {
      id: '2',
      title: 'Nova prestação enviada 2',
      description: 'Prestação 2025/01 foi enviada para análise.',
      createdAt: '2025-12-10T09:00:00Z',
      read: true,
    },
  ]);

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const unreadNotifications = notifications.filter((n) => !n.read);
  const hasUnread = unreadNotifications.length > 0;

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  // Clique fora fecha o dropdown
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const itemsToShow =
    activeTab === 'unread' ? unreadNotifications : notifications;

  return (
    <div ref={wrapperRef} className="relative">
      <button
        onClick={handleToggle}
        className="relative p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        aria-label="Abrir notificações"
      >
        <Bell className="w-5 h-5 text-gray-700 dark:text-gray-300" />

        {hasUnread && (
          <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center px-1.5 min-w-[18px] h-[18px] rounded-full bg-red-500 text-[10px] font-semibold text-white">
            {unreadNotifications.length}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-lg z-50">
          {/* Cabeçalho */}
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Notificações
            </span>
            <button
              onClick={handleClose}
              className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
              aria-label="Fechar notificações"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex px-4 py-3 space-x-2 text-xs font-medium border-b border-gray-200 dark:border-gray-800">
            <button
              onClick={() => setActiveTab('unread')}
              className={`px-3 py-1 rounded-lg border ${
                activeTab === 'unread'
                  ? 'bg-blue-100 text-blue-700 border-blue-200'
                  : 'bg-transparent text-gray-600 dark:text-gray-300 border-transparent hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              Não lidas ({unreadNotifications.length})
            </button>
            <button
              onClick={() => setActiveTab('all')}
              className={`px-3 py-1 rounded-lg border ${
                activeTab === 'all'
                  ? 'bg-blue-100 text-blue-700 border-blue-200'
                  : 'bg-transparent text-gray-600 dark:text-gray-300 border-transparent hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              Todas ({notifications.length})
            </button>
          </div>

          {/* Lista / estado vazio */}
          <div className="px-4 py-4 max-h-80 overflow-y-auto">
            {itemsToShow.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                <Bell className="w-8 h-8 mb-2 text-gray-300 dark:text-gray-600" />
                {activeTab === 'unread' ? (
                  <span>Nenhuma notificação não lida</span>
                ) : (
                  <span>Nenhuma notificação</span>
                )}
              </div>
            ) : (
              <ul className="space-y-2">
                {itemsToShow.map((notification) => (
                  <li
                    key={notification.id}
                    className="rounded-lg px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                  >
                    <div className="flex items-start justify-between">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {notification.title}
                      </p>
                      {!notification.read && (
                        <span className="mt-1 ml-2 h-2 w-2 rounded-full bg-blue-500" />
                      )}
                    </div>
                    {notification.description && (
                      <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
                        {notification.description}
                      </p>
                    )}
                    <p className="mt-1 text-[11px] text-gray-400">
                      {new Date(notification.createdAt).toLocaleString()}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
